import { useEffect, useMemo, useRef, useState } from "react";
import {
  baixarCsv,
  lerFiltros,
  lerMatriculas,
  montarCsvDeMatriculas,
  TETO_DA_PLANILHA,
  GAVETAS_DE_PRAZO,
  ROTULO_GAVETA_DE_PRAZO,
  gavetaDaQuery,
  type FiltrosDoAdminPos,
  type GavetaDePrazo,
  type MatriculaDoAdmin,
} from "../lib/adminPos";
import { CampoDeBusca } from "./ui";
import { BarraDeFiltros, CampoDeData, Filtro, LimparFiltros, LinhaDeFiltros } from "./BarraDeFiltros";
import Carregando from "./Carregando";

// Admin › Faculdade › Matrículas — com filtros e exportação.
//
// Pedido da reunião de 31/08: *"painel para cuidar das matrículas (com filtros e
// opção de exportar planilha)"*.
//
// ---------------------------------------------------------------------------
// As duas contagens que a tabela mostra respondem a pergunta REAL
// ---------------------------------------------------------------------------
// "Disciplinas na grade" e "aprovadas" vêm do banco na mesma linha, com a MESMA
// `pos_nota_minima()` da trava 4 do certificado. É o que permite a operadora
// responder "este aluno pode receber o certificado?" sem abrir outra tela — e o
// número não pode ser recalculado aqui, porque duas contas divergiriam e a tela
// diria "8 de 8" ao lado de uma trava que barra.
//
// ---------------------------------------------------------------------------
// A INSTITUIÇÃO tem três estados, e o terceiro é filtrável
// ---------------------------------------------------------------------------
// `aluno_focus` era a pergunta 5 do mapeamento, aberta desde 31/07, e o painel da
// secretaria a responde numa linha: `true` → Focus, senão FBC. Medido: 14.117 de
// 41.979 são Focus (33,6%). Mas **336 pedidos da origem não casam com matrícula
// nenhuma**, e a regra do painel os rotula FBC por omissão — aqui a coluna é
// NULÁVEL e o filtro "não informada" existe para a equipe achar exatamente esses.
// O que não se alcança não se corrige.
//
// ---------------------------------------------------------------------------
// "Meses desde o início" pode vir NEGATIVO, e isso é um achado
// ---------------------------------------------------------------------------
// Medido em 01/09: **10 matrículas têm `inicio` no futuro**, até 9 meses à frente.
// A trava 5 as barra, o que é o desfecho certo por acaso — mas a data está
// errada. Esta tela é onde elas aparecem, com o número negativo à vista em vez de
// escondido num `Math.max(0, …)`.

const POR_PAGINA = 50;

export default function AdminPosMatriculas() {
  const [filtros, setFiltros] = useState<FiltrosDoAdminPos | null>(null);
  const [linhas, setLinhas] = useState<MatriculaDoAdmin[]>([]);
  const [fase, setFase] = useState<"carregando" | "pronto" | "erro">("carregando");
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [curso, setCurso] = useState("");
  const [turma, setTurma] = useState("");
  const [situacao, setSituacao] = useState("");
  const [instituicao, setInstituicao] = useState("");
  // 000206: o período (sobre o início) e o prazo máximo do curso.
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [prazo, setPrazo] = useState<GavetaDePrazo | null>(null);
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(0);

  const [exportando, setExportando] = useState<string | null>(null);

  const [buscaAtiva, setBuscaAtiva] = useState("");
  const relogio = useRef<number | null>(null);
  useEffect(() => {
    if (relogio.current) window.clearTimeout(relogio.current);
    relogio.current = window.setTimeout(() => setBuscaAtiva(busca), 400);
    return () => {
      if (relogio.current) window.clearTimeout(relogio.current);
    };
  }, [busca]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const f = await lerFiltros();
        if (vivo) setFiltros(f);
      } catch {
        if (vivo) setFase("erro");
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    setPagina(0);
  }, [curso, turma, situacao, instituicao, buscaAtiva, de, ate, prazo]);

  const criterios = useMemo(
    () => ({
      curso: curso || null,
      turma: turma || null,
      situacao: situacao || null,
      instituicao: instituicao || null,
      busca: buscaAtiva || null,
      de: de || null,
      ate: ate || null,
      prazo,
    }),
    [curso, turma, situacao, instituicao, buscaAtiva, de, ate, prazo],
  );

  useEffect(() => {
    let vivo = true;
    setBuscando(true);
    (async () => {
      try {
        const r = await lerMatriculas({
          ...criterios,
          limite: POR_PAGINA,
          offset: pagina * POR_PAGINA,
        });
        if (!vivo) return;
        setLinhas(r);
        setErro(null);
        setFase("pronto");
      } catch (e) {
        if (!vivo) return;
        setErro(e instanceof Error ? e.message : "não deu para carregar as matrículas");
        setFase("pronto");
      } finally {
        if (vivo) setBuscando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [criterios, pagina]);

  // A lista de turmas NÃO existe antes do curso — regra de Victor em 14/09:
  // *"jamais mostrar a lista de turmas sem antes selecionar o curso"*. A versão
  // anterior filtrava com `!curso ||`, o que mostrava as **59 turmas** quando
  // nenhum curso estava escolhido.
  //
  // O número que faz a regra ser regra: as 59 usam só **20 números**, e "Turma
  // 1" existe em **9 cursos**. Como o rótulo é `Turma <n> · <m> matriculados`,
  // sem o curso a lista oferece nove opções com o mesmo nome — o operador não
  // tem como escolher, e o que ele escolher vai parecer certo.
  const turmasDoCurso = useMemo(
    () => (curso ? (filtros?.turmas ?? []).filter((t) => t.curso_slug === curso) : []),
    [filtros, curso],
  );

  function trocarCurso(novo: string) {
    setCurso(novo);
    setTurma("");
  }

  async function exportar() {
    setExportando("Montando a planilha…");
    try {
      const r = await montarCsvDeMatriculas(criterios, (lidas, alvo) =>
        setExportando(`Montando a planilha… ${lidas} de ${alvo}`),
      );
      const nome = ["matriculas", curso || "todos-os-cursos", hoje()].join("-") + ".csv";
      baixarCsv(r.csv, nome);
      setExportando(
        r.truncado
          ? // Truncamento DITO, nunca silencioso. Um arquivo com 20.000 de 43.077
            // linhas que não avisa é pior que um erro: a operadora usaria como se
            // fosse a base inteira.
            `Baixado com ${r.linhas.toLocaleString("pt-BR")} de ${r.total.toLocaleString("pt-BR")} linhas — o limite por exportação é ${TETO_DA_PLANILHA.toLocaleString("pt-BR")}. Estreite o filtro (por curso ou turma) para levar o resto.`
          : `Baixado: ${r.linhas.toLocaleString("pt-BR")} linha${r.linhas === 1 ? "" : "s"}.`,
      );
    } catch (e) {
      setExportando(
        `Não deu para montar a planilha: ${e instanceof Error ? e.message : "erro desconhecido"}`,
      );
    }
  }

  if (fase === "carregando" && !filtros) return <Carregando />;
  if (fase === "erro") {
    return (
      <p className="rounded-xl border border-erro-borda bg-erro-fundo p-4 text-sm text-erro-tinta">
        Não conseguimos abrir o painel de matrículas. Recarregue a página.
      </p>
    );
  }
  if (filtros === null) {
    return (
      <p className="rounded-xl border border-borda bg-superficie p-4 text-sm text-cinza">
        Este painel é da equipe da secretaria. Se você deveria vê-lo, fale com o Victor.
      </p>
    );
  }

  const total = linhas[0]?.total_no_filtro ?? 0;
  const primeira = pagina * POR_PAGINA;

  return (
    // O espaçamento dos blocos é do CONTAINER, o MESMO das três sub-abas
    // (`gap-5`). Ver o comentário equivalente em `AdminPosNotas.tsx`: com a
    // margem em cada bloco, a barra de filtros — que não tem margem própria,
    // porque é peça compartilhada — ficava grudada na tabela.
    <section className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-titulo">Matrículas</h2>
          <p className="mt-1 text-sm text-cinza">
            {buscando && linhas.length === 0
              ? "Buscando…"
              : total === 0
                ? "Nenhuma matrícula com esses filtros."
                : `${primeira + 1}–${primeira + linhas.length} de ${total.toLocaleString("pt-BR")} matrícula${total === 1 ? "" : "s"}`}
          </p>
        </div>
        <button
          type="button"
          data-exportar
          onClick={exportar}
          disabled={total === 0 || exportando?.startsWith("Montando") === true}
          className="pill-cta text-sm disabled:cursor-not-allowed disabled:opacity-50"
        >
          {exportando?.startsWith("Montando") ? "Montando…" : "Exportar planilha"}
        </button>
      </header>

      {exportando && !exportando.startsWith("Montando") && (
        <p className="rounded-lg border border-borda bg-chip/50 p-3 text-sm text-tinta">
          {exportando}
        </p>
      )}

      {/* A MESMA barra do balcão e das notas (000206), com o período e o prazo
          que Victor pediu para as três sub-abas. Ver o comentário equivalente em
          `AdminPosNotas.tsx`: rótulo na opção "todos", nome acessível no
          `aria-label`, e sem contagem nas opções. */}
      <BarraDeFiltros>
        <LinhaDeFiltros n={1}>
          <CampoDeBusca
            valor={busca}
            aoMudar={setBusca}
            placeholder="Buscar por nome, e-mail ou CPF"
            rotulo="Buscar aluno por nome, e-mail ou CPF"
            aoLimpar={busca ? () => setBusca("") : undefined}
            data-filtro="busca"
            className="min-w-[18rem] flex-1"
          />
          <Filtro
            rotulo="Curso"
            rotuloTodos="Todos os cursos"
            chave="curso"
            valor={curso || null}
            aoTrocar={(v) => trocarCurso(v ?? "")}
            opcoes={filtros.cursos.map((c) => ({ valor: c.slug, rotulo: c.nome ?? c.slug }))}
          />
          <Filtro
            rotulo="Turma"
            rotuloTodos={curso ? "Todas as turmas" : "Escolha o curso primeiro"}
            desligado={!curso}
            chave="turma"
            valor={turma || null}
            aoTrocar={(v) => setTurma(v ?? "")}
            // A contagem de matriculados é o TAMANHO da turma, não "quantos
            // itens este filtro devolve" — ela fica.
            opcoes={turmasDoCurso.map((t) => ({
              valor: t.id,
              rotulo: `Turma ${t.numero} · ${t.matriculados}`,
            }))}
          />
          <Filtro
            rotulo="Situação"
            rotuloTodos="Todas as situações"
            chave="situacao"
            valor={situacao || null}
            aoTrocar={(v) => setSituacao(v ?? "")}
            opcoes={filtros.situacoes.map((s) => ({ valor: s, rotulo: s }))}
          />
        </LinhaDeFiltros>

        <LinhaDeFiltros n={2}>
          <Filtro
            rotulo="Instituição"
            rotuloTodos="Todas as instituições"
            chave="instituicao"
            valor={instituicao || null}
            aoTrocar={(v) => setInstituicao(v ?? "")}
            opcoes={[
              { valor: "fbc", rotulo: "FBC" },
              { valor: "focus", rotulo: "Focus" },
              // O terceiro estado é filtrável de propósito — "não sei" não é
              // FBC, e o que não se alcança não se corrige (000137).
              { valor: "sem", rotulo: "Não informada" },
            ]}
          />
          {/* O período é sobre o INÍCIO da matrícula, que é a data que a tabela
              mostra e a que a operadora usa ("as matrículas de agosto"). */}
          <CampoDeData
            rotulo="Início a partir de"
            chave="de"
            valor={de || null}
            min={filtros.periodo_matriculas?.de}
            max={ate || filtros.periodo_matriculas?.ate}
            aoTrocar={(v) => setDe(v ?? "")}
          />
          <CampoDeData
            rotulo="Início até"
            chave="ate"
            valor={ate || null}
            min={de || filtros.periodo_matriculas?.de}
            max={filtros.periodo_matriculas?.ate}
            aoTrocar={(v) => setAte(v ?? "")}
          />
          {/* O prazo MÁXIMO do curso (18 meses na CGT, 24 nos outros sete).
              Medido em 09/09: 3.750 das 43.394 matrículas já o estouraram. É
              informação e nunca trava — a 000144 decidiu isso, porque 73,4% das
              matrículas da CGT estão fora do prazo e recusar certificado a elas
              seria o erro que não tem volta. */}
          <Filtro
            rotulo="Prazo do curso"
            rotuloTodos="Todos os prazos"
            chave="prazo"
            valor={prazo}
            aoTrocar={(v) => setPrazo(gavetaDaQuery(v))}
            opcoes={GAVETAS_DE_PRAZO.map((g) => ({ valor: g, rotulo: ROTULO_GAVETA_DE_PRAZO[g] }))}
          />
        </LinhaDeFiltros>

        {(curso || turma || situacao || instituicao || busca || de || ate || prazo) && (
          <LimparFiltros
            aoLimpar={() => {
              setCurso("");
              setTurma("");
              setSituacao("");
              setInstituicao("");
              setBusca("");
              setDe("");
              setAte("");
              setPrazo(null);
            }}
          />
        )}
      </BarraDeFiltros>

      {erro && (
        <p className="rounded-lg border border-aviso-borda bg-aviso-fundo p-3 text-sm text-aviso-tinta">
          {erro} — a lista abaixo é a da consulta anterior.
        </p>
      )}

      <div className="overflow-x-auto rounded-xl border border-borda bg-superficie">
        <table data-tabela-matriculas className="w-full min-w-[58rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-borda text-left">
              <th scope="col" className="p-3 font-semibold text-tinta">Aluno</th>
              <th scope="col" className="p-3 font-semibold text-tinta">Curso</th>
              <th scope="col" className="p-3 font-semibold text-tinta">Turma</th>
              <th scope="col" className="p-3 font-semibold text-tinta">Situação</th>
              <th scope="col" className="p-3 font-semibold text-tinta">Instituição</th>
              <th scope="col" className="p-3 font-semibold text-tinta">Início e prazo</th>
              <th scope="col" className="p-3 text-right font-semibold text-tinta">Aprovadas</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((m) => (
              <tr
                key={m.id}
                data-matricula-linha={m.id}
                className="border-b border-borda/60 last:border-0"
              >
                <td className="p-3">
                  <span className="block font-semibold text-tinta">{m.aluno_nome ?? "—"}</span>
                  <span className="block text-xs text-cinza">{m.aluno_email}</span>
                </td>
                <td className="p-3 text-tinta">{m.curso_nome ?? m.curso_slug}</td>
                <td className="p-3 whitespace-nowrap text-cinza">
                  {m.turma_numero !== null ? `Turma ${m.turma_numero}` : "—"}
                </td>
                <td className="p-3 text-cinza">{m.situacao}</td>
                <td className="p-3 whitespace-nowrap">
                  {m.instituicao === "focus" ? (
                    <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-tinta">
                      Focus
                    </span>
                  ) : m.instituicao === "fbc" ? (
                    <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-tinta">
                      FBC
                    </span>
                  ) : (
                    // "não informada", e não vazio: célula vazia é lida como
                    // defeito da tela, e este é um FATO.
                    <span className="text-xs text-cinza">não informada</span>
                  )}
                </td>
                <td className="p-3 whitespace-nowrap text-cinza">
                  {m.inicio ? new Date(`${m.inicio}T12:00:00`).toLocaleDateString("pt-BR") : "—"}
                  {m.meses_desde_o_inicio !== null && (
                    <span
                      data-meses={m.meses_desde_o_inicio}
                      className={`block text-xs ${
                        m.meses_desde_o_inicio < 0 ? "font-semibold text-erro-tinta" : ""
                      }`}
                    >
                      {m.meses_desde_o_inicio < 0
                        ? // À VISTA, não escondido: a data está no futuro, e são
                          // 10 casos reais que só esta tela pode achar.
                          `data no futuro (${Math.abs(m.meses_desde_o_inicio)} ${Math.abs(m.meses_desde_o_inicio) === 1 ? "mês" : "meses"})`
                        : `${m.meses_desde_o_inicio} ${m.meses_desde_o_inicio === 1 ? "mês" : "meses"}`}
                    </span>
                  )}
                  {/* A gaveta de PRAZO na linha (000206): filtrar por "fora do
                      prazo" e a lista não dizer qual está fora é a tela
                      contradizendo o próprio filtro. Só aparece quando NÃO é
                      "dentro" — carimbar "dentro do prazo" em 39.644 linhas é a
                      informação que não informa (a cicatriz do "Pode ser MEI"
                      471 vezes).

                      `alerta`, e não `erro`: estar fora do prazo máximo NÃO
                      impede o certificado (000144, com 73,4% da CGT nesse
                      estado). Vermelho diria ao operador que há um problema a
                      resolver, e o que há é um fato a saber. */}
                  {m.prazo !== "dentro" && (
                    <span
                      data-prazo={m.prazo}
                      className="mt-0.5 inline-block rounded-full bg-alerta-fundo px-2 py-0.5 text-xs font-semibold text-alerta-tinta"
                    >
                      {m.prazo === "estourou" ? "fora do prazo do curso" : "sem prazo definido"}
                    </span>
                  )}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {m.disciplinas_da_turma > 0 ? (
                    <span
                      data-aprovadas={`${m.aprovadas}/${m.disciplinas_da_turma}`}
                      className={`font-semibold tabular-nums ${
                        m.aprovadas >= m.disciplinas_da_turma ? "text-sucesso" : "text-tinta"
                      }`}
                    >
                      {m.aprovadas} de {m.disciplinas_da_turma}
                    </span>
                  ) : (
                    // Grade não publicada. Dizer "0 de 0" seria lido como "o
                    // aluno não fez nada", e o problema é outro.
                    <span className="text-xs text-cinza">sem grade</span>
                  )}
                </td>
              </tr>
            ))}
            {linhas.length === 0 && !buscando && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-sm text-cinza">
                  Nenhuma matrícula com esses filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > POR_PAGINA && (
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            data-pagina-anterior
            disabled={pagina === 0 || buscando}
            onClick={() => setPagina((p) => Math.max(0, p - 1))}
            className="btn-contorno text-sm disabled:cursor-not-allowed disabled:opacity-45"
          >
            ← Anteriores
          </button>
          <span className="text-xs text-cinza">
            página {pagina + 1} de {Math.max(1, Math.ceil(total / POR_PAGINA))}
          </span>
          <button
            type="button"
            data-pagina-proxima
            disabled={primeira + linhas.length >= total || buscando}
            onClick={() => setPagina((p) => p + 1)}
            className="btn-contorno text-sm disabled:cursor-not-allowed disabled:opacity-45"
          >
            Próximas →
          </button>
        </div>
      )}
    </section>
  );
}

function hoje(): string {
  const d = new Date();
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0")].join(
    "-",
  );
}
